/**
 * Dashboard Service
 * API calls for dashboard statistics
 */

import api from '@/lib/api';

export interface TicketStats {
  total_tickets: number;
  new_tickets: number;
  open_tickets: number;
  pending_tickets: number;
  in_progress_tickets: number;
  solved_tickets: number;
  closed_tickets: number;
  urgent_tickets: number;
  high_priority_tickets: number;
  normal_priority_tickets: number;
  low_priority_tickets: number;
  unassigned_tickets: number;
  spam_tickets: number;
  created_last_24h: number;
  created_last_7d: number;
  resolved_last_24h: number;
  resolved_last_7d: number;
  avg_resolution_minutes: number | null;
  avg_first_response_minutes: number | null;
}

export interface SLAStats {
  total_tracked: number;
  first_response_met: number;
  first_response_breached: number;
  resolution_met: number;
  resolution_breached: number;
  first_response_compliance_pct: number | null;
  resolution_compliance_pct: number | null;
}

export interface AgentStats {
  total_agents: number;
  active_agents: number;
}

export interface TodayStats {
  tickets_created_today: number;
  tickets_resolved_today: number;
}

export interface DashboardOverview {
  tickets: TicketStats;
  sla: SLAStats;
  agents: AgentStats;
  today: TodayStats;
  period: {
    days: number;
    from: string;
    to: string;
  };
}

export interface TicketTrend {
  period: string;
  created: number;
  resolved: number;
  urgent: number;
  high_priority: number;
}

export interface AgentPerformance {
  id: string;
  name: string;
  email: string;
  role: string;
  user_status: string;
  last_login: string | null;
  assigned_tickets: number;
  resolved_tickets: number;
  open_tickets: number;
  urgent_tickets: number;
  avg_resolution_minutes: number | null;
  avg_first_response_minutes: number | null;
  total_replies: number;
  public_replies: number;
  internal_notes: number;
  resolution_rate: number | null;
  sla: {
    total_sla_tracked: number;
    first_response_met: number;
    first_response_breached: number;
    resolution_met: number;
    resolution_breached: number;
  };
}

export interface TagAnalytics {
  tag: string;
  usage_count: number;
  tag_color: string | null;
}

export interface OrganizationStats {
  id: string;
  name: string;
  total_tickets: number;
  open_tickets: number;
  resolved_tickets: number;
  urgent_tickets: number;
  avg_resolution_minutes: number | null;
}

export interface RecentActivity {
  id: string;
  ticket_id: string;
  ticket_number: string;
  ticket_subject: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  changed_by: string;
}

export interface Distribution {
  status?: string;
  priority?: string;
  count: number;
  percentage: number;
}

export interface SystemTagAnalytics {
  id: string;
  name: string;
  description: string | null;
  color: string;
  usage_count: number;
  last_used_at: string | null;
  ticket_count: number;
  open_ticket_count: number;
  resolved_ticket_count: number;
  urgent_count: number;
  high_priority_count: number;
  avg_confidence: number | null;
  percentage: number;
  // Hierarchical fields
  parent_id?: string;
  tag_level?: number;
  parent_name?: string;
  parent_color?: string;
  full_path?: string;
}

export interface SystemTagAnalyticsResponse {
  systemTags: SystemTagAnalytics[];
  summary: {
    totalTaggedTickets: number;
    uniqueTags: number;
  };
  period: {
    days: number;
  };
}

class DashboardService {
  async getOverview(days: number = 30, organizationId?: string): Promise<DashboardOverview> {
    const params = new URLSearchParams({ days: days.toString() });
    if (organizationId) params.append('organizationId', organizationId);

    const response = await api.get(`/dashboard/overview?${params.toString()}`);
    return response.data.data;
  }

  async getTicketTrends(days: number = 30, groupBy: 'hour' | 'day' | 'week' | 'month' = 'day', organizationId?: string): Promise<TicketTrend[]> {
    const params = new URLSearchParams({ days: days.toString(), groupBy });
    if (organizationId) params.append('organizationId', organizationId);

    const response = await api.get(`/dashboard/ticket-trends?${params.toString()}`);
    return response.data.data.trends;
  }

  async getAgentPerformance(days: number = 30, organizationId?: string): Promise<AgentPerformance[]> {
    const params = new URLSearchParams({ days: days.toString() });
    if (organizationId) params.append('organizationId', organizationId);

    const response = await api.get(`/dashboard/agent-performance?${params.toString()}`);
    return response.data.data.agents;
  }

  async getTagAnalytics(days: number = 30, limit: number = 10): Promise<TagAnalytics[]> {
    const params = new URLSearchParams({ days: days.toString(), limit: limit.toString() });

    const response = await api.get(`/dashboard/tag-analytics?${params.toString()}`);
    return response.data.data.tags;
  }

  async getOrganizationStats(days: number = 30): Promise<OrganizationStats[]> {
    const params = new URLSearchParams({ days: days.toString() });

    const response = await api.get(`/dashboard/organization-stats?${params.toString()}`);
    return response.data.data.organizations;
  }

  async getRecentActivity(limit: number = 20): Promise<RecentActivity[]> {
    const params = new URLSearchParams({ limit: limit.toString() });

    const response = await api.get(`/dashboard/recent-activity?${params.toString()}`);
    return response.data.data.activities;
  }

  async getPriorityDistribution(days: number = 30): Promise<Distribution[]> {
    const params = new URLSearchParams({ days: days.toString() });

    const response = await api.get(`/dashboard/priority-distribution?${params.toString()}`);
    return response.data.data.distribution;
  }

  async getStatusDistribution(days: number = 30): Promise<Distribution[]> {
    const params = new URLSearchParams({ days: days.toString() });

    const response = await api.get(`/dashboard/status-distribution?${params.toString()}`);
    return response.data.data.distribution;
  }

  async getSystemTagAnalytics(days: number = 30, limit: number = 15, organizationId?: string): Promise<SystemTagAnalyticsResponse> {
    const params = new URLSearchParams({ days: days.toString(), limit: limit.toString() });
    if (organizationId) params.append('organizationId', organizationId);

    const response = await api.get(`/dashboard/system-tag-analytics?${params.toString()}`);
    return response.data.data;
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
